import React, { useMemo } from "react";

import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CDRInformationModel, informationSchema, temperatures, equipmentTypes, pictureDescription, conditionCodes } from "./schema";

import Button from "../../themed/button";
import CreatableMultiselectDropdown from "../../themed/multiselect/creatable";
import DatePickerInput from "../../themed/date-picker";
import Input from "../../themed/input/input";
import Select from "../../themed/select/select";

import useProjectDetails from "../../../hooks/useProjectDetails";

interface Props {
    cdrInformation?: CDRInformationModel;
    onSaveCDRInformation: (cdrInformation: CDRInformationModel) => void;
}

const CDRInformation: React.FC<Props> = ({ cdrInformation, onSaveCDRInformation }) => {
    const { projectDetails } = useProjectDetails();
    const { control, formState, handleSubmit } = useForm<CDRInformationModel>({
        resolver: zodResolver(informationSchema),
        defaultValues: cdrInformation,
        mode: "all"
    });

    const modules = useMemo(() => {
        return projectDetails ? projectDetails.facility.modules.map(({ name }) =>({ label: name, value: name })) : [];
    }, [projectDetails]);

    return ( 
        <form onSubmit={handleSubmit(onSaveCDRInformation)}>
            <div className="container-fluid">
                <div className="row">
                    <div className="col">
                        <Controller
                            control={control}
                            name="tagNumber"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Tag Number"
                                    placeholder="Enter Tag Number"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.tagNumber ? formState.errors.tagNumber.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="identificationInformation"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Identification Information"
                                    placeholder="Enter Identification Information"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.identificationInformation ? formState.errors.identificationInformation.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            defaultValue={projectDetails?.facilityName}
                            name="installation"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Installation"
                                    placeholder="Enter Installation"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.installation ? formState.errors.installation.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="systemNumber"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="System Number"
                                    placeholder="Enter System Number"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.systemNumber ? formState.errors.systemNumber.message : ''}
                                />
                            )}
                        />
                    </div>
                </div>

                <div className="divider my-4"></div>

                <div className="row">
                    <div className="col">
                        <Controller
                            control={control}
                            name="functionalLocation"
                            defaultValue="NG.BON"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Functional Location"
                                    placeholder="Enter Functional Location"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.functionalLocation ? formState.errors.functionalLocation.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="pressure"
                            render={({ field }) => (
                                <Input
                                    type="number"
                                    label="Pressure (Barg)"
                                    placeholder="Enter Pressure (Barg)"
                                    value={field.value}
                                    onChange={(event) => field.onChange(event.target.valueAsNumber)}
                                    name={field.name}
                                    error={formState.errors.pressure ? formState.errors.pressure.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="temperature"
                            render={({ field }) => (
                                <Select
                                    label="Temperature (C)"
                                    placeholder="Select Temperature (C)"
                                    options={temperatures.map((temperature) => ({ id: temperature, name: temperature }))}
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.temperature ? formState.errors.temperature.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="process"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Process"
                                    placeholder="Enter Process"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.process ? formState.errors.process.message : ''}
                                />
                            )}
                        />
                    </div>
                </div>
                
                <div className="divider my-4"></div>

                <div className="row">
                    <div className="col">
                        <Controller
                            control={control}
                            name="pidNumber"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="P&ID Number"
                                    placeholder="Enter P&ID Number"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.pidNumber ? formState.errors.pidNumber.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="module"
                            render={({ field }) => (
                                <CreatableMultiselectDropdown
                                    label="Module"
                                    placeholder="Select Module"
                                    isMulti={false}
                                    isLoading={false}
                                    closeMenuOnSelect
                                    isSearchable
                                    options={modules}
                                    onSelect={(option) => field.onChange(option)}
				                    selectedValues={field.value}
                                    error={formState.errors.module ? formState.errors.module.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="location"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Location"
                                    placeholder="Enter Location"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.location ? formState.errors.location.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="pictureDescription"
                            render={({ field }) => (
                                <Select
                                    label="Picture Description"
                                    placeholder="Select Picture Description"
                                    options={pictureDescription.map((option) => ({ id: option, name: option }))}
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.pictureDescription ? formState.errors.pictureDescription.message : ''}
                                />
                            )}
                        />
                    </div>
                </div>
                
                <div className="divider my-4"></div>

                <div className="row">
                    <div className="col">
                        <Controller
                            control={control}
                            name="equipmentType"
                            render={({ field }) => (
                                <Select
                                    label="Type of Equipment"
                                    placeholder="Select Type of Equipment"
                                    options={equipmentTypes.map((option) => ({ id: option, name: option }))}
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.equipmentType ? formState.errors.equipmentType.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="conditionCode"
                            render={({ field }) => (
                                <Select
                                    label="Condition Code"
                                    placeholder="Select Condition Code"
                                    options={conditionCodes.map((option) => ({ id: option, name: option }))}
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.conditionCode ? formState.errors.conditionCode.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="category"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Category"
                                    placeholder="Enter Category"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.category ? formState.errors.category.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="ramAssessment"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="RAM Assessment"
                                    placeholder="Enter RAM Assessment"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.ramAssessment ? formState.errors.ramAssessment.message : ''}
                                />
                            )}
                        />
                    </div>
                </div>
                
                <div className="divider my-4"></div>

                <div className="row">
                    <div className="col">
                        <Controller
                            control={control}
                            name="anomalyDescription"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Anomaly Description"
                                    placeholder="Enter Anomaly Description"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.anomalyDescription ? formState.errors.anomalyDescription.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="remedialAction"
                            render={({ field }) => (
                                <Input
                                    type="text"
                                    label="Remedial Action"
                                    placeholder="Enter Remedial Action"
                                    value={field.value}
                                    onChange={field.onChange}
                                    name={field.name}
                                    error={formState.errors.remedialAction ? formState.errors.remedialAction.message : ''}
                                />
                            )}
                        />
                    </div>
                    
                    <div className="col">
                        <Controller
                            control={control}
                            name="dateOfFinding"
                            render={({ field }) => (
                                <DatePickerInput
                                    label="Date of Finding"
                                    placeholderText="Select Date of Finding"
                                    date={field.value}
                                    onDateChange={field.onChange}
                                    maxDate={new Date()}
                                    error={formState.errors.dateOfFinding ? formState.errors.dateOfFinding.message : ''}
                                />
                            )}
                        />
                    </div>
                </div>

                <div className="mt-4 flex justify-content-end align-item-center">
                    <Button
                        type="submit"
                        disabled={!formState.isValid}
                        label={formState.isSubmitting ? "Saving Information..." : "Save Information"}
                    />
                </div>
            </div>
        </form>
    );
};

export default CDRInformation;